import { IconClock, IconPhoto } from "@tabler/icons-react";
import { Text } from "../../../../components/common/typography";
import { Chip } from "../../../../components/common/chip";
import { SectionCard } from "../../../../components/common/section-card";
import { resolveImageSource } from "../../../../components/common/image";

export interface VariantDisplayRow {
  id: number;
  variant: string;
  sku: string;
  price: number;
  stock: number;
  discount: number;
  imageUrl?: string;
}

export interface VariantPendingOverlay {
  price?: number;
  stock?: number;
  image?: boolean;
  discount?: number;
  archive?: boolean;
}

/** A brand-new variant proposed in a pending change request — not yet live. */
export interface PendingNewVariantRow {
  /** Resolved axis label, e.g. "1 TB / Black". */
  name: string;
  sku?: string;
  price?: number;
  stock?: number;
  discount?: number;
  hasImage?: boolean;
}

interface Props {
  variants: VariantDisplayRow[];
  pendingOverlay?: Map<number, VariantPendingOverlay>;
  /** New variants awaiting admin approval; rendered as labeled rows below the live variants. */
  pendingNewVariants?: PendingNewVariantRow[];
}

function AwaitingApprovalChip() {
  return (
    <Chip variant="inReview" icon={<IconClock size={12} />}>
      Awaiting approval
    </Chip>
  );
}

function PendingNote({ children }: { children: React.ReactNode }) {
  return (
    <div className="mt-1 flex items-center gap-1">
      <IconClock size={12} className="shrink-0 text-accentAmber" />
      <Text variant="bodyXs" tone="amber" inline>
        {children}
      </Text>
    </div>
  );
}

export function DetailsVariantsSection({ variants, pendingOverlay, pendingNewVariants = [] }: Props) {
  const cellClass =
    "grid grid-cols-[56px_minmax(0,2fr)_repeat(4,minmax(0,1fr))] items-start gap-4 border-t border-borderDefault px-6 py-4";
  return (
    <SectionCard shadow title="Variants & pricing" className="gap-5">
      <Text variant="bodySm" tone="dark">{variants.length} variants generated from 2 options</Text>
      <div className="overflow-hidden rounded-xl border border-borderDefault">
        {/* Phones: keep the columns readable and scroll the table sideways. */}
        <div className="overflow-x-auto">
        <div className="max-md:min-w-[640px]">
        <div className="grid grid-cols-[56px_minmax(0,2fr)_repeat(4,minmax(0,1fr))] gap-4 bg-bgLight px-6 py-3">
          {["Image", "Variant", "SKU", "Price (QAR)", "Stock", "Discount %"].map((h) => (
            <Text key={h} variant="capsBig" tone="dark">{h}</Text>
          ))}
        </div>
        {variants.map((v) => {
          const pending = pendingOverlay?.get(v.id);
          const hasPending =
            pending != null &&
            (pending.archive ||
              pending.image ||
              pending.price !== undefined ||
              pending.stock !== undefined ||
              pending.discount !== undefined);
          return (
            <div key={v.id} className={cellClass}>
              <div className="flex size-12 items-center justify-center overflow-hidden rounded-xl border border-borderDefault bg-bgLight">
                {v.imageUrl ? (
                  <img src={resolveImageSource(v.imageUrl) ?? undefined} alt={v.variant} className="size-full object-cover" />
                ) : (
                  <IconPhoto size={20} className="text-textMuted" />
                )}
              </div>
              <div className="flex min-w-0 flex-col gap-1.5">
                <span className="block truncate" title={v.variant}>
                  <Text variant="bodyBold" tone="dark" inline>{v.variant}</Text>
                </span>
                {hasPending && (
                  <div>
                    <AwaitingApprovalChip />
                  </div>
                )}
                {pending?.archive && (
                  <PendingNote>Pending: will be archived</PendingNote>
                )}
                {pending?.image && !pending?.archive && (
                  <PendingNote>Pending: new image</PendingNote>
                )}
              </div>
              <span className="min-w-0 truncate" title={v.sku}>
                <Text variant="body" tone="muted" inline>{v.sku}</Text>
              </span>
              <div>
                <Text variant="body" tone="muted" inline>{v.price.toLocaleString()}</Text>
                {pending?.price !== undefined && (
                  <PendingNote>Pending: {pending.price.toLocaleString()}</PendingNote>
                )}
              </div>
              <div>
                <Text variant="body" tone="dark" inline>{v.stock}</Text>
                {pending?.stock !== undefined && (
                  <PendingNote>Pending: {pending.stock}</PendingNote>
                )}
              </div>
              <div>
                <Text variant="body" tone={v.discount > 0 ? "accent" : "muted"} inline>
                  {v.discount > 0 ? `${v.discount}%` : "—"}
                </Text>
                {pending?.discount !== undefined && (
                  <PendingNote>Pending: {pending.discount > 0 ? `${pending.discount}%` : "0%"}</PendingNote>
                )}
              </div>
            </div>
          );
        })}

        {/* New variants proposed in a pending change request — not live yet, so
            they're shown here on an amber-tinted row clearly marked as awaiting
            approval rather than being invisible until the admin approves. */}
        {pendingNewVariants.map((nv, i) => (
          <div key={`new-${i}`} className={`${cellClass} bg-accentYellowTint/30`}>
            <div className="flex size-12 items-center justify-center overflow-hidden rounded-xl border border-dashed border-accentAmber bg-bgLight">
              <IconPhoto size={20} className="text-accentAmber" />
            </div>
            <div className="flex min-w-0 flex-col gap-1.5">
              <span className="block truncate" title={nv.name}>
                <Text variant="bodyBold" tone="dark" inline>{nv.name}</Text>
              </span>
              <div>
                <AwaitingApprovalChip />
              </div>
              <PendingNote>New variant — not live until approved</PendingNote>
              {nv.hasImage && <PendingNote>Pending: image attached</PendingNote>}
            </div>
            <span className="min-w-0 truncate" title={nv.sku ?? "—"}>
              <Text variant="body" tone="muted" inline>{nv.sku || "—"}</Text>
            </span>
            <div>
              <Text variant="body" tone="muted" inline>
                {nv.price !== undefined ? nv.price.toLocaleString() : "—"}
              </Text>
            </div>
            <div>
              <Text variant="body" tone="muted" inline>
                {nv.stock !== undefined ? nv.stock : "—"}
              </Text>
            </div>
            <div>
              <Text variant="body" tone={nv.discount ? "accent" : "muted"} inline>
                {nv.discount ? `${nv.discount}%` : "—"}
              </Text>
            </div>
          </div>
        ))}
        </div>
        </div>
      </div>
    </SectionCard>
  );
}
